import * as React from 'react'
import axios from 'axios'
import { useDebounce } from 'react-use'
import { csrfToken, formatPrice } from 'helpers'
import { useLocale } from 'hooks'
import {
  getPricingSuggestions,
  getQueuePlacement,
  DEBOUNCE_DELAY,
  PricingSuggestion
} from '../../selling/common'
import PriceAdjustContent from './PriceAdjustContent'
import * as S from './InlinePriceAdjust.styles'

export interface InlinePriceAdjustProps {
  collectionId: number
  productId: number
  sizeConversionId: number
  sellerId: number
  currentPriceInCents: number
  currencyCode: string
  minimumPrice: number
  expanded: boolean
  handlingFee?: number
  paymentFee?: number
  shippingFee?: number
  onPriceChange?: (formattedPrice: string) => void
  onClose?: () => void
  onLastSoldFetched?: (formatted: string) => void
}

type SaveState = 'idle' | 'saving' | 'saved' | 'error'

const InlinePriceAdjustPanel: React.FC<InlinePriceAdjustProps> = ({
  collectionId,
  productId,
  sizeConversionId,
  sellerId,
  currentPriceInCents,
  currencyCode,
  minimumPrice,
  expanded,
  handlingFee,
  paymentFee,
  shippingFee,
  onPriceChange,
  onClose,
  onLastSoldFetched
}) => {
  const { localiseUrl } = useLocale()
  const [suggestion, setSuggestion] = React.useState<PricingSuggestion | null>(
    null
  )
  const [lowestQueuePrice, setLowestQueuePrice] = React.useState<number | null>(
    null
  )
  const [queuePosition, setQueuePosition] = React.useState<number | null>(null)
  const [lowestPriceCurrency, setLowestPriceCurrency] =
    React.useState<string>(currencyCode)
  const [loading, setLoading] = React.useState(false)
  const [hasFetched, setHasFetched] = React.useState(false)
  const [saveState, setSaveState] = React.useState<SaveState>('idle')
  const [pendingPrice, setPendingPrice] = React.useState<number | null>(null)

  // Fetch pricing data lazily — only when panel is expanded
  React.useEffect(() => {
    if (!expanded || hasFetched) return

    const fetchData = async () => {
      setLoading(true)
      try {
        const [suggestionResult, queueResult] = await Promise.all([
          getPricingSuggestions({
            productId,
            sizeConversionId,
            sellerId
          }),
          getQueuePlacement(
            {
              productId,
              priceInCents: currentPriceInCents,
              excludeId: collectionId,
              sizeConversionId,
              sellerId
            },
            () => {}
          )
        ])

        const effectiveSuggestion = suggestionResult ?? {
          price: currentPriceInCents / 100,
          label: 'last_sold' as const
        }

        setSuggestion(effectiveSuggestion)

        if (suggestionResult) {
          onLastSoldFetched?.(
            formatPrice(suggestionResult.price, { currencyCode })
          )
        }

        if (queueResult?.data?.lowest_price != null) {
          setLowestQueuePrice(queueResult.data.lowest_price)
        }

        if (queueResult?.data?.place_in_queue != null) {
          setQueuePosition(queueResult.data.place_in_queue)
        }

        if (queueResult?.data?.lowest_price_currency) {
          setLowestPriceCurrency(queueResult.data.lowest_price_currency)
        }

        setHasFetched(true)
      } catch {
        // Silently fail
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [
    expanded,
    hasFetched,
    productId,
    sizeConversionId,
    sellerId,
    currentPriceInCents,
    currencyCode,
    onLastSoldFetched
  ])

  // Reset pending price when panel opens
  React.useEffect(() => {
    if (expanded) {
      setPendingPrice(null)
      setSaveState('idle')
    }
  }, [expanded])

  // Re-fetch queue placement when the user adjusts the price via slider
  const updateQueuePlacement = React.useCallback(async () => {
    if (pendingPrice === null) return

    const priceInCents = Math.round(pendingPrice * 100)

    if (priceInCents === currentPriceInCents) return

    try {
      const queueResult = await getQueuePlacement(
        {
          productId,
          priceInCents,
          excludeId: collectionId,
          sizeConversionId,
          sellerId
        },
        () => {}
      )

      if (queueResult?.data?.place_in_queue != null) {
        setQueuePosition(queueResult.data.place_in_queue)
      }

      if (queueResult?.data?.lowest_price != null) {
        setLowestQueuePrice(queueResult.data.lowest_price)
      }

      if (queueResult?.data?.lowest_price_currency) {
        setLowestPriceCurrency(queueResult.data.lowest_price_currency)
      }
    } catch {
      // Silently fail
    }
  }, [pendingPrice, currentPriceInCents, productId, sizeConversionId, sellerId])

  useDebounce(updateQueuePlacement, DEBOUNCE_DELAY, [pendingPrice])

  const handlePriceSelect = React.useCallback((price: number) => {
    setPendingPrice(price)
  }, [])

  const handleMatchLastSold = React.useCallback(() => {
    if (suggestion?.price) {
      setPendingPrice(suggestion.price)
    }
  }, [suggestion])

  const handleSave = React.useCallback(() => {
    if (pendingPrice === null || saveState === 'saving') return

    const priceInCents = Math.round(pendingPrice * 100)

    if (priceInCents === currentPriceInCents || priceInCents <= 0) return

    setSaveState('saving')
    axios
      .patch(
        localiseUrl(`/api/selling/${collectionId}/set_price`, {
          localeAsParam: true
        }),
        {
          price_in_cents: priceInCents,
          authenticity_token: csrfToken
        }
      )
      .then((response) => {
        setSaveState('saved')
        if (response.data?.price) {
          onPriceChange?.(response.data.price)
        }

        // Auto-close panel after successful save
        onClose?.()
      })
      .catch(() => {
        setSaveState('error')
      })
  }, [
    pendingPrice,
    saveState,
    currentPriceInCents,
    collectionId,
    localiseUrl,
    onPriceChange,
    onClose
  ])

  if (!expanded) return null

  return (
    <S.SliderPanel>
      <PriceAdjustContent
        suggestion={suggestion}
        minimumPrice={minimumPrice}
        lowestQueuePrice={lowestQueuePrice}
        currentPriceInCents={currentPriceInCents}
        currencyCode={currencyCode}
        selectedPrice={pendingPrice}
        onPriceSelect={handlePriceSelect}
        onMatchLastSold={handleMatchLastSold}
        onSave={handleSave}
        saveState={saveState}
        loading={loading}
        sellerId={sellerId}
        productId={productId}
        queuePosition={queuePosition}
        lowestPriceCurrency={lowestPriceCurrency}
        handlingFee={handlingFee}
        paymentFee={paymentFee}
        shippingFee={shippingFee}
      />
    </S.SliderPanel>
  )
}

export default InlinePriceAdjustPanel

export { S as InlinePriceAdjustStyles }
